import { useMutation } from '@tanstack/react-query';
import { useConsentInfo, useServiceUrls, useSettings } from './queryHooks';
import { useAppState, useBankSearchState, useErrorState, useSelectedProvider } from './zustandHooks';
import {
  accessTokenStorage,
  connectApi,
  consentConnectionService,
  consentService,
  recaptchaService,
  userIdStorage,
  validationTokenStorage
} from '../domain/services';
import qs from 'qs';
import { useHistory } from 'react-router';
import ErrorScreenTypeEnum from '../types/enums/errorScreenTypeEnum';
import { AxiosError } from 'axios';
import { ICreateTuUserParams } from '../domain/services/IConnectApi';
import { DATA_SOURCE_TYPE, DataSourceType } from '../types/DataSourceType';
import { KbaAnswersSubmit } from '../containers/kba/interfaces';
import { useDeviceRisk } from './useDeviceRisk';
import { trackEvent } from '../App';
import { EventTypes } from '../utils/applicationInsights';
import { useCreditAccountsFetchStatusReceiver } from './useCreditAccountsFetchStatusReceiver';

export const mutationKeys = {
  createStoredUserMutation: 'create_stored_user_mutation',
  createConsentMutation: 'create_consent_mutation',
  consentConnectionMutation: 'consent_connection_mutation',
  reconfirmConsentMutation: 'reconfirm_consent_mutation',
  revokeConsentMutation: 'revoke_consent_mutation',
  reauthenticationConsentMutation: 'reauthentication_consent_mutation',
  createTuUser: 'create_tu_user',
  sendOtpMutation: 'send_otp_mutation',
  validateMutation: 'validate_otp_mutation',
  sendKbaAnswers: 'send_kba_answers_mutation',
  createTuRegistrationMutation: 'create_tu-registration-mutation'
};

export const useStoredInvitationMutation = () => {
  const { serviceUrls } = useServiceUrls();
  const { appState, setAppState } = useAppState();
  const blackBox = useDeviceRisk().blackbox;
  const history = useHistory();
  const { mutateAsync } = useCreateConsentMutation();
  const creditAccountsFetchStatusReceiver = useCreditAccountsFetchStatusReceiver();

  return useMutation({
    mutationKey: [mutationKeys.createStoredUserMutation],
    onError() {
      trackEvent({ type: EventTypes.CIS_STORED_INVITE_VALIDATION_FAILED });
      history.push('/account-lookup', { hasFailedValidation: true });
    },
    onSuccess(data) {
      if (data.tuUserId) setAppState({ tuUserId: data.tuUserId });
    },
    mutationFn: async () => {
      if (!appState?.invitationId) throw Error('no invitation id found.');
      await mutateAsync({
        providerId: 0,
        name: 'CIS',
        dataSource: 'CIS',
        dataSourceType: DATA_SOURCE_TYPE.CreditInformation
      });

      await creditAccountsFetchStatusReceiver.connect();

      trackEvent({ type: EventTypes.CIS_SUBMIT_STORED_INVITE });
      return await connectApi.createStoredUser({
        applicationId: appState.clientId,
        deviceRisk: { blackBox },
        invitationId: appState?.invitationId,
        url: serviceUrls.connectApiService
      });
    }
  });
};

export const useCreateConsentMutation = () => {
  const selectedProvider = useSelectedProvider(state => state.selectedProvider);
  const selectedCountry = useBankSearchState(state => state.selectedCountry);
  const setError = useErrorState(state => state.setError);
  const { serviceUrls } = useServiceUrls();
  const { appState, setAppState } = useAppState();

  const history = useHistory();

  return useMutation({
    mutationKey: [mutationKeys.createConsentMutation],
    onError(error) {
      const err = error as AxiosError;
      trackEvent({
        type: EventTypes.CREATE_CONSENT_ID_ERROR,
        payload: { errMsg: err.message, cause: err.cause, ...err }
      });

      if (err.status === 400 || error.message.includes('parameter_missing_error')) {
        setError({
          type: ErrorScreenTypeEnum.LoadingError,
          code: 'parameter_missing_error'
        });
        history.push('/error');
      }
    },
    onSuccess(data) {
      setAppState({ consentId: data.consentId });
      if (data.userId && !userIdStorage.hasUserId()) {
        userIdStorage.setUserId(data.userId);
      }
      if (!appState.useValidationToken) recaptchaService.reset();
      trackEvent({
        type: EventTypes.CREATE_CONSENT_ID_SUCCESS,
        payload: { userId: data.userId, consentId: data.consentId }
      });
    },
    mutationFn: async (params: {
      providerId?: number;
      name?: string;
      dataSource?: string;
      dataSourceType?: DataSourceType;
    }) => {
      if (!appState.customerReference || !appState.clientId) {
        throw new Error('parameter_missing_error', {
          cause: `${!appState.customerReference ? 'customer_ref' : 'client_id'} not present`
        });
      }
      let validationToken;
      let userId = userIdStorage.getUserId();

      validationToken = appState.useValidationToken
        ? validationTokenStorage.getValidationToken()
        : await recaptchaService.triggerAsync();

      if (!validationToken) throw Error('no validation token found.');

      const providerId = params?.providerId ?? selectedProvider?.providerId!;
      const name = params?.name ?? selectedProvider?.name!;
      const dataSource = params?.dataSource ?? selectedProvider?.dataSource!;
      const dataSourceType: DataSourceType = params?.dataSourceType ?? DATA_SOURCE_TYPE.OpenBanking;
      if (dataSourceType === DATA_SOURCE_TYPE.OpenBanking) {
        return await consentService.createConsent(
          serviceUrls?.consentService!,
          appState.clientId,
          appState.customerReference,
          validationToken as string,
          providerId,
          name,
          dataSource,
          dataSourceType,
          selectedCountry,
          appState.configurationId,
          selectedProvider?.maxConsentDays,
          appState.invitationId,
          userId
        );
      } else {
        return await connectApi.createConsent(
          serviceUrls?.connectApiService!,
          appState.clientId,
          appState.customerReference,
          validationToken as string,
          providerId,
          name,
          dataSource,
          dataSourceType,
          selectedCountry,
          appState.configurationId,
          selectedProvider?.maxConsentDays,
          appState.invitationId,
          userId
        );
      }
    }
  });
};

export const useConsentConnectionMutation = () => {
  const { serviceUrls } = useServiceUrls();
  const setAppState = useAppState(state => state.setAppState);
  const consentId = useAppState(state => state.appState.consentId);
  const clientId = useAppState(state => state.appState.clientId);
  const { mutateAsync } = useCreateConsentMutation();

  return useMutation({
    mutationKey: [mutationKeys.consentConnectionMutation],
    onSuccess(data) {
      setAppState({ redirectUrl: data.redirectUrl });
      trackEvent({
        type: EventTypes.CREATE_CONSENT_ID_CONNECTION_SUCCESS,
        payload: { redirectUrl: data.redirectUrl }
      });
      if (window.hj && consentId && clientId) {
        window.hj('identify', clientId, { consentId });
      }
    },
    onError(error) {
      const err = error as AxiosError;
      trackEvent({
        type: EventTypes.CREATE_CONSENT_ID_CONNECTION_ERROR,
        payload: { ...err }
      });
    },
    mutationFn: async () => {
      const data = await mutateAsync({});
      return await consentConnectionService.createConsentConnection(serviceUrls?.gatewayService!, data.consentId);
    }
  });
};

export const useReconfirmConsentConnection = () => {
  const { serviceUrls } = useServiceUrls();
  const { consentInfo } = useConsentInfo();
  const { settings } = useSettings();
  const history = useHistory();
  const setAppState = useAppState(state => state.setAppState);
  const consentId = useAppState(state => state.appState.consentId);

  return useMutation({
    mutationKey: [mutationKeys.reconfirmConsentMutation],
    onSuccess() {
      const redirectUrl = appendReconfirmQueryParamsToRedirectUri(
        true,
        settings?.redirectUrl,
        consentInfo?.customerReference,
        consentId
      );
      setAppState({ redirectUrl });
      history.push('/end-success');
    },
    mutationFn: async () => {
      return await consentConnectionService.reconfirmConsentConnection(
        serviceUrls?.gatewayService!,
        consentId!,
        accessTokenStorage.getToken()!
      );
    }
  });
};

export const useRevokeConsentMutation = () => {
  const { serviceUrls } = useServiceUrls();
  const { settings } = useSettings();
  const history = useHistory();

  const setAppState = useAppState(state => state.setAppState);
  const consentId = useAppState(state => state.appState.consentId);
  const customerReference = useAppState(state => state.appState.customerReference);

  return useMutation({
    mutationKey: [mutationKeys.revokeConsentMutation],
    onSuccess() {
      setAppState({
        redirectUrl: appendRevokeQueryParamsToRedirectUri(settings?.redirectUrl, 'consent_revoked', customerReference),
        consentRevokeSuccess: true
      });
      history.push('/end-success');
    },
    mutationFn: async () => {
      return await consentService.revokeConsent(
        serviceUrls?.consentService!,
        consentId!,
        accessTokenStorage.getToken()!
      );
    }
  });
};

export const useCreateTuUserMutation = () => {
  const { serviceUrls } = useServiceUrls();
  const clientId = useAppState(state => state.appState.clientId);
  const invitationId = useAppState(state => state.appState.invitationId);
  const setAppState = useAppState(state => state.setAppState);
  const { mutate } = useSendOtpMutation();
  const userId = userIdStorage.getUserId() as string;

  return useMutation({
    mutationKey: [mutationKeys.createTuUser],
    onError() {
      trackEvent({ type: EventTypes.CIS_VALIDATION_FAILED });
      return;
    },
    onSuccess(data) {
      if (data.tuUserId) setAppState({ tuUserId: data.tuUserId });
      if (data.status === 'OtpRequired') {
        mutate();
      }
    },
    mutationFn: async (params: Omit<ICreateTuUserParams, 'url' | 'applicationId' | 'userId' | 'invitationId'>) => {
      return await connectApi.createTuUser({
        ...params,
        userId: userId,
        ...(invitationId && { invitationId }),
        url: serviceUrls?.connectApiService!,
        applicationId: clientId
      });
    }
  });
};

export const useSendOtpMutation = () => {
  const { serviceUrls } = useServiceUrls();
  const history = useHistory();
  const clientId = useAppState(state => state.appState.clientId);
  const tuUserId = useAppState(state => state.appState.tuUserId);
  const userId = userIdStorage.getUserId() as string;

  return useMutation({
    mutationKey: [mutationKeys.sendOtpMutation],
    onError() {
      history.push('/error');
    },
    mutationFn: async () => {
      if (!tuUserId) throw Error('no tuUserId found.');
      trackEvent({ type: EventTypes.CIS_OTP_SEND });
      return await connectApi.sendOtp(serviceUrls?.connectApiService!, clientId, userId, tuUserId);
    }
  });
};

export const useValidateOtpMutation = () => {
  const { serviceUrls } = useServiceUrls();
  const history = useHistory();
  const clientId = useAppState(state => state.appState.clientId);
  const tuUserId = useAppState(state => state.appState.tuUserId);
  const userId = userIdStorage.getUserId() as string;

  return useMutation({
    mutationKey: [mutationKeys.validateMutation],
    onError() {
      history.push('/error');
    },
    mutationFn: async (code: string) => {
      if (!tuUserId) throw Error('no tuUserId found.');
      trackEvent({ type: EventTypes.CIS_OTP_VALIDATE });
      const result = await connectApi.validateOtp(serviceUrls?.connectApiService!, clientId, userId, tuUserId, code);

      if (result.status === 'Expired') trackEvent({ type: EventTypes.CIS_OTP_EXPIRED });
      else if (result.status === 'Retry') trackEvent({ type: EventTypes.CIS_OTP_RETRY });
      else if (result.status === 'Failed') trackEvent({ type: EventTypes.CIS_OTP_VALIDATION_FAILED });
      else if (result.status === 'Verified') trackEvent({ type: EventTypes.CIS_OTP_VALIDATION_COMPLETE });

      return result;
    }
  });
};

export const useReauthenticateConsentConnectionMutation = () => {
  const { serviceUrls } = useServiceUrls();
  const history = useHistory();
  const setAppState = useAppState(state => state.setAppState);
  const consentId = useAppState(state => state.appState.consentId);
  return useMutation({
    mutationKey: [mutationKeys.reauthenticationConsentMutation],
    onSuccess(data) {
      setAppState({ redirectUrl: data.redirectUrl });
    },
    onError() {
      history.push('/error');
    },
    mutationFn: async () => {
      return await consentConnectionService.reauthenticateConsentConnection(
        serviceUrls?.gatewayService!,
        consentId!,
        accessTokenStorage.getToken()!
      );
    }
  });
};

// utils
function appendReconfirmQueryParamsToRedirectUri(
  isSuccessful: boolean,
  redirectUri: string | undefined,
  customerReference?: string | undefined,
  consentId?: string | undefined,
  errorCode?: any | undefined
) {
  if (!redirectUri) return;

  const url = new URL(redirectUri);
  const queryParameters = qs.parse(url.search, {
    ignoreQueryPrefix: true,
    decoder: decodeURIComponent
  });

  if (isSuccessful) {
    queryParameters['state'] = 'success';
    queryParameters['consent_id'] = consentId;
  } else {
    queryParameters['state'] = 'error';
    queryParameters['error'] = errorCode?.response?.data?.code;
  }
  queryParameters['customer_ref'] = customerReference;

  url.search = qs.stringify(queryParameters);

  return url.toString();
}

function appendRevokeQueryParamsToRedirectUri(
  redirectUri: string | undefined,
  error?: any | undefined,
  customerReference?: string | undefined
) {
  if (!redirectUri) return;

  const url = new URL(redirectUri);
  const queryParameters = qs.parse(url.search, {
    ignoreQueryPrefix: true,
    decoder: decodeURIComponent
  });

  queryParameters['state'] = 'error';
  queryParameters['customer_ref'] = customerReference;
  queryParameters['error'] = error;

  url.search = qs.stringify(queryParameters);

  return url.toString();
}

export const useSendKbaAnswersMutation = () => {
  const { serviceUrls } = useServiceUrls();
  const history = useHistory();
  const clientId = useAppState(state => state.appState.clientId);
  const tuUserId = useAppState(state => state.appState.tuUserId) as string;
  const userId = userIdStorage.getUserId() as string;
  return useMutation({
    mutationKey: [mutationKeys.sendKbaAnswers],
    onError() {
      history.push('/error');
    },
    mutationFn: async (kbaAnswers: KbaAnswersSubmit[]) => {
      trackEvent({ type: EventTypes.CIS_KBA_VALIDATE });
      let result = await connectApi.sendKbaAnswers(
        serviceUrls?.connectApiService!,
        userId,
        tuUserId,
        clientId,
        kbaAnswers
      );

      if (result.status === 'Failed') trackEvent({ type: EventTypes.CIS_KBA_VALIDATION_FAILED });
      else if (result.status === 'Retry') trackEvent({ type: EventTypes.CIS_KBA_RETRY });
      else if (result.status === 'Verified') trackEvent({ type: EventTypes.CIS_KBA_VALIDATION_COMPLETE });
      else if (result.status === 'RequiresAdditionalQuestions')
        trackEvent({ type: EventTypes.CIS_KBA_REQUIRE_ADDITIONAL_QUESTIONS });

      return result;
    }
  });
};
